import * as React from "react";
import * as PropTypes from "prop-types";

const rowsHeight = 24;

function getStyles(props: any, context: any, state: any) {
  return {
    root: {
      position: "relative", // because the shadow has position: 'absolute'
    },
    textarea: {
      height: state.height,
      width: "100%",
      resize: "none",
      font: "inherit",
      padding: 0,
      cursor: "inherit",
      border: "none",
      outline: "none",
      background: "transparent",
      color: "inherit",
    },
    shadow: {
      resize: "none",
      // Overflow also needed to here to remove the extra row
      // added to textareas in Firefox.
      overflow: "hidden",
      // Visibility needed to hide the extra text area on ipads
      visibility: "hidden",
      position: "absolute",
      height: "auto",
    },
  };
}

class EnhancedTextarea extends React.Component<any, any, {muiTheme: any}> {
  static propTypes = {
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    hintText: PropTypes.node,
    onChange: PropTypes.func,
    onHeightChange: PropTypes.func,
    rows: PropTypes.number,
    rowsMax: PropTypes.number,
    shadowStyle: PropTypes.object,
    /**
     * Override the inline-styles of the root element.
     */
    style: PropTypes.object,
    textareaStyle: PropTypes.object,
    value: PropTypes.string,
    valueLink: PropTypes.object,
  };

  static defaultProps = {
    rows: 1,
  };

  state = {
    height: null as any,
  };

  UNSAFE_componentWillMount() {
    this.setState({
      height: this.props.rows * rowsHeight,
    });
  }

  componentDidMount() {
    this.syncHeightWithShadow(this.props.value);
    window.addEventListener("resize", this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  UNSAFE_componentWillReceiveProps(nextProps: any) {
    if (nextProps.value !== this.props.value ||
        nextProps.rowsMax !== this.props.rowsMax) {
      this.syncHeightWithShadow(nextProps.value, null, nextProps);
    }
  }

  handleResize = (event?: any) => {
    this.syncHeightWithShadow(this.props.value, event);
  };

  getInputNode() {
    return this.refs.input;
  }

  setValue(value: any) {
    (this.getInputNode() as any).value = value;
    this.syncHeightWithShadow(value);
  }

  syncHeightWithShadow(newValue: any, event: any = undefined, props: any = undefined) {
    const shadow: any = this.refs.shadow;
    const displayText = this.props.hintText && (newValue === "" || newValue === undefined || newValue === null) ?
      this.props.hintText : newValue;

    if (displayText !== undefined) {
      shadow.value = displayText;
    }

    let newHeight = shadow.scrollHeight;

    // Guarding for jsdom, where scrollHeight isn't present.
    // See https://github.com/tmpvar/jsdom/issues/1013
    if (newHeight === undefined) return;

    props = props || this.props;

    if (props.rowsMax >= props.rows) {
      newHeight = Math.min(props.rowsMax * rowsHeight, newHeight);
    }

    newHeight = Math.max(newHeight, rowsHeight);

    if (this.state.height !== newHeight) {
      const input: any = this.refs.input;
      const cursorPosition = input.selectionStart;
      this.setState({
        height: newHeight,
      }, () => {
        if (input === document.activeElement) {
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      });

      if (props.onHeightChange) {
        props.onHeightChange(event, newHeight);
      }
    }
  }

  handleChange = (event: any) => {
    if (!this.props.hasOwnProperty("value")) {
      this.syncHeightWithShadow(event.target.value);
    }

    if (this.props.hasOwnProperty("valueLink")) {
      this.props.valueLink.requestChange(event.target.value);
    }

    if (this.props.onChange) {
      this.props.onChange(event);
    }
  };

  render() {
    const {
      onChange, // eslint-disable-line no-unused-vars
      onHeightChange, // eslint-disable-line no-unused-vars
      rows, // eslint-disable-line no-unused-vars
      rowsMax, // eslint-disable-line no-unused-vars
      shadowStyle,
      style,
      hintText, // eslint-disable-line no-unused-vars
      textareaStyle,
      valueLink, // eslint-disable-line no-unused-vars
      ...other
    } = this.props;

    const styles = getStyles(this.props, this.context, this.state);
    const rootStyles = Object.assign(styles.root, style);
    const textareaStyles = Object.assign(styles.textarea, textareaStyle);
    const shadowStyles = Object.assign({}, textareaStyles, styles.shadow, shadowStyle);
    const overrides: {[key:string] : any} = {};

    if (this.props.hasOwnProperty("valueLink")) {
      overrides.value = this.props.valueLink.value
    }

    return (
      <div style={(rootStyles)}>
        <textarea
          ref="shadow"
          style={(shadowStyles)}
          tabIndex={-1}
          rows={this.props.rows}
          defaultValue={this.props.defaultValue}
          readOnly={true}
          value={this.props.value}
        />
        <textarea
          {...other}
          {...overrides}
          ref="input"
          rows={this.props.rows}
          style={(textareaStyles)}
          onChange={this.handleChange}
        />
      </div>
    );
  }
}

export default EnhancedTextarea;
